import { Button, Card, Group, rem, Stack, Text } from '@mantine/core'
import { IconPlus } from '@tabler/icons-react'
import { MaintenanceTask } from './utils'
import { TaskListItem } from './TaskListItem'

interface TaskCardProps {
  title: string
  tasks: MaintenanceTask[]
  completedCount: number
  totalCount: number
  taskType: 'user' | 'markku'
  onTaskClick: (task: MaintenanceTask) => void
  onAddTask?: () => void
  emptyMessage: string
}

export function TaskCard({
  title,
  tasks,
  completedCount,
  totalCount,
  taskType,
  onTaskClick,
  onAddTask,
  emptyMessage,
}: TaskCardProps) {
  const isUserCard = taskType === 'user'
  const cardStyles = isUserCard
    ? {
        backgroundColor: 'var(--mantine-color-white)',
        borderColor: undefined,
      }
    : {
        backgroundColor: 'var(--mantine-color-green-0)',
        borderColor: 'var(--mantine-color-green-2)',
      }

  return (
    <Card
      withBorder
      radius="lg"
      padding="lg"
      style={{
        ...cardStyles,
        minHeight: rem(120),
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" fw={600} c="gray.7">
            {title} ({completedCount}/{totalCount})
          </Text>
          {onAddTask && (
            <Button
              variant="light"
              size="sm"
              leftSection={<IconPlus size={14} />}
              onClick={onAddTask}
            >
              Lisää tehtävä
            </Button>
          )}
        </Group>

        <Stack gap="xs">
          {tasks.length === 0 ? (
            <Text c="dimmed" size="sm" ta="center" mt="md">
              {emptyMessage}
            </Text>
          ) : (
            <>
              {isUserCard ? (
                // User tasks: simple list
                tasks.map((task) => (
                  <TaskListItem
                    key={task.id}
                    task={task}
                    onClick={onTaskClick}
                    variant="user"
                  />
                ))
              ) : (
                <>
                  {/* Markku tasks: scheduled first, then completed */}
                  {tasks
                    .filter((task) => !task.completed)
                    .map((task) => (
                      <TaskListItem
                        key={task.id}
                        task={task}
                        onClick={onTaskClick}
                        variant="markku-scheduled"
                        backgroundColor="var(--mantine-color-white)"
                        borderColor="var(--mantine-color-green-3)"
                        hoverBackgroundColor="var(--mantine-color-green-1)"
                        hoverBorderColor="var(--mantine-color-green-4)"
                      />
                    ))}

                  {tasks
                    .filter((task) => task.completed)
                    .map((task) => (
                      <TaskListItem
                        key={task.id}
                        task={task}
                        onClick={onTaskClick}
                        variant="markku-completed"
                        backgroundColor="var(--mantine-color-green-0)"
                        borderColor="var(--mantine-color-green-2)"
                        hoverBackgroundColor="var(--mantine-color-green-1)"
                        hoverBorderColor="var(--mantine-color-green-3)"
                      />
                    ))}
                </>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}
